import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, ArrowRight, Check, Copy, Send, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth-context';
import { supabase } from '../lib/supabase';
import { copyToClipboard } from '../utils/clipboard';
import {
  requestTensionResponse,
  requestTensionResponseStream,
  type TensionContextType,
  type TensionEmotion,
  type TensionRespondPayload,
  type TensionStreamPartialEvent,
} from '../lib/tension-api';
import { Body, Button, Card, Caption, ChoiceChip, Headline, Input, Title } from '@/design-system/components';

type ScreenStep = 1 | 2 | 3 | 4 | 5 | 6;
type RelationshipType = 'dating' | 'committed' | 'married' | 'situationship';

const CONTEXT_OPTIONS: Array<{ id: TensionContextType; label: string }> = [
  { id: 'misunderstanding', label: 'misunderstanding' },
  { id: 'tone_issue', label: 'tone issue' },
  { id: 'emotional_distance', label: 'emotional distance' },
  { id: 'argument_starting', label: 'argument starting' },
  { id: 'other', label: 'other' },
];

const EMOTION_OPTIONS: Array<{ id: TensionEmotion; label: string }> = [
  { id: 'confused', label: 'confused' },
  { id: 'overwhelmed', label: 'overwhelmed' },
  { id: 'hurt', label: 'hurt' },
  { id: 'frustrated', label: 'frustrated' },
  { id: 'anxious', label: 'anxious' },
];

const RELATIONSHIP_TYPES: Array<{ id: RelationshipType; label: string }> = [
  { id: 'dating', label: 'Dating' },
  { id: 'committed', label: 'Committed' },
  { id: 'married', label: 'Married' },
  { id: 'situationship', label: 'Situationship' },
];

const GOAL_OPTIONS = [
  'De-escalate conflict faster',
  'Rebuild trust and reliability',
  'Improve day-to-day communication',
  'Feel more emotionally safe',
  'Stay connected under stress',
];

const PAIN_POINT_OPTIONS = [
  'Tone gets misread',
  'We avoid hard conversations',
  'Small issues escalate quickly',
  'Repair after conflict takes too long',
  'External stress spills into us',
];

const COMMUNICATION_STYLES = ['Direct and clear', 'Soft and careful', 'Depends on context', 'Still figuring it out'];
const CONFLICT_STYLES = ['Pursue quickly', 'Need space first', 'Depends on context', 'Still figuring it out'];
const AFFECTION_STYLES = ['Words of affirmation', 'Quality time', 'Acts of care', 'Physical touch', 'Mixed style'];
const CADENCE_OPTIONS = ['daily', '3x weekly', 'weekly'];

function safeLocalStorageSet(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function toggleSelection(current: Array<string>, value: string, max = 3): Array<string> {
  if (current.includes(value)) {
    return current.filter((entry) => entry !== value);
  }
  if (current.length >= max) {
    return current;
  }
  return [...current, value];
}

function buildFallbackResponse(context: TensionContextType, emotion: TensionEmotion): TensionRespondPayload['response'] {
  const insight =
    context === 'tone_issue'
      ? 'This likely came from tone mismatch, not rejection.'
      : context === 'argument_starting'
        ? 'This looks like escalation pressure, not a dead end.'
        : 'This feels like a misinterpretation, not direct conflict.';
  const reframe = 'A short reset message can reduce defensiveness quickly.';
  const suggestedMessages =
    emotion === 'overwhelmed'
      ? ["I'm overwhelmed right now, not upset with you."]
      : ["I think this landed wrong. I'm not against you, I want us clear."];
  const groundingLine = 'One calm sentence is enough right now.';
  return {
    insight,
    reframe,
    suggestedMessages,
    groundingLine,
    sharing: {
      sendThis: suggestedMessages[0],
      shareMomentQuote: suggestedMessages[0],
      povCaption: 'When you mean one thing, but it lands differently.',
    },
  };
}

export default function OnboardingCompressed() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [step, setStep] = useState<ScreenStep>(1);
  const [sessionId] = useState(() => crypto.randomUUID());

  // Profile and relationship basics.
  const [partnerName, setPartnerName] = useState('');
  const [partnerPronouns, setPartnerPronouns] = useState('');
  const [relationshipType, setRelationshipType] = useState<RelationshipType | null>(null);
  const [relationshipStartDate, setRelationshipStartDate] = useState('');

  // Goals and pain points.
  const [goals, setGoals] = useState<Array<string>>([]);
  const [goalCustom, setGoalCustom] = useState('');
  const [painPoints, setPainPoints] = useState<Array<string>>([]);
  const [painPointCustom, setPainPointCustom] = useState('');

  // Dynamics and routines.
  const [communicationStyle, setCommunicationStyle] = useState('');
  const [conflictStyle, setConflictStyle] = useState('');
  const [affectionStyle, setAffectionStyle] = useState('');
  const [checkInCadence, setCheckInCadence] = useState('daily');
  const [reminderTime, setReminderTime] = useState('19:00');
  const [stressors, setStressors] = useState('');
  const [boundaries, setBoundaries] = useState('');
  const [supportPreference, setSupportPreference] = useState('');

  // Moment + response.
  const [context, setContext] = useState<TensionContextType | null>(null);
  const [emotion, setEmotion] = useState<TensionEmotion | null>(null);
  const [contextDetails, setContextDetails] = useState('');
  const [responseData, setResponseData] = useState<TensionRespondPayload | null>(null);
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);
  const [responseRevealStage, setResponseRevealStage] = useState(0);
  const [livePhaseLabel, setLivePhaseLabel] = useState<string>('');
  const [partialResponse, setPartialResponse] = useState<TensionStreamPartialEvent | null>(null);

  const progress = useMemo(() => Math.round((step / 6) * 100), [step]);
  const response = responseData?.response ?? null;

  useEffect(() => {
    if (step !== 6 || !response) return;
    setResponseRevealStage(0);
    const t1 = window.setTimeout(() => setResponseRevealStage(1), 200);
    const t2 = window.setTimeout(() => setResponseRevealStage(2), 850);
    const t3 = window.setTimeout(() => setResponseRevealStage(3), 1450);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [step, response]);

  const goToStep = (nextStep: ScreenStep) => {
    setStep(nextStep);
  };

  const nextFromStepOne = () => {
    if (!partnerName.trim() || !relationshipType) {
      toast.error('Add partner name and relationship type to continue.');
      return;
    }
    goToStep(2);
  };

  const nextFromStepTwo = () => {
    if (goals.length === 0 && !goalCustom.trim()) {
      toast.error('Select at least one relationship goal.');
      return;
    }
    goToStep(3);
  };

  const nextFromStepThree = () => {
    if (!communicationStyle || !conflictStyle || !affectionStyle) {
      toast.error('Choose communication, conflict, and affection styles.');
      return;
    }
    goToStep(4);
  };

  const nextFromStepFour = () => {
    if (!checkInCadence || !reminderTime) {
      toast.error('Set your check-in cadence and reminder time.');
      return;
    }
    goToStep(5);
  };

  const runTensionEngine = async () => {
    if (!context || !emotion) {
      toast.error('Pick a situation and feeling first.');
      return;
    }

    setIsLoadingResponse(true);
    setLivePhaseLabel('Listening');
    setPartialResponse(null);
    goToStep(6);
    try {
      const situationText = contextDetails.trim() || context.replace(/_/g, ' ');
      const apiResponse = await requestTensionResponseStream(
        {
          sessionId,
          input: {
            situationText,
            contextType: context,
            emotion,
            relationshipMode: 'relationship',
          },
          options: { stream: true },
        },
        {
          onState: (event) => {
            setLivePhaseLabel(event.label);
          },
          onPartial: (event) => {
            setPartialResponse(event);
          },
        },
      );
      setResponseData(apiResponse);
      if (!apiResponse.performance.withinTarget) {
        toast.info('Response generated. Optimizing for faster next response.');
      }
    } catch (error) {
      console.error(error);
      try {
        const situationText = contextDetails.trim() || context.replace(/_/g, ' ');
        const apiResponse = await requestTensionResponse({
          sessionId,
          input: {
            situationText,
            contextType: context,
            emotion,
            relationshipMode: 'relationship',
          },
          options: { stream: false },
        });
        setResponseData(apiResponse);
        setLivePhaseLabel('response ready');
        return;
      } catch {
        // continue to local fallback
      }
      const fallback = buildFallbackResponse(context, emotion);
      setResponseData({
        ok: true,
        sessionId,
        response: fallback,
        context: {
          classification: 'unclear_intent',
          riskLevel: 'medium',
        },
        memory: {
          emotionTags: [emotion],
          patternCounts: {},
          lastInsightSummary: fallback.insight,
        },
        performance: {
          latencyMs: 0,
          targetMs: 1500,
          withinTarget: true,
        },
      });
      setLivePhaseLabel('fallback response');
      toast.warning('Using fallback clarity mode. Check AI key/config for full engine output.');
    } finally {
      setIsLoadingResponse(false);
    }
  };

  const copyShareValue = async (text: string, success: string) => {
    const ok = await copyToClipboard(text);
    if (ok) toast.success(success);
    else toast.error('Copy failed. Try again.');
  };

  const shareMoment = async (text: string) => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'TENSION moment', text });
        toast.success('Shared successfully.');
        return;
      } catch {
        // Fall through to clipboard fallback.
      }
    }
    await copyShareValue(text, 'Copied. Ready to share.');
  };

  const finishOnboarding = async () => {
    if (!context || !emotion || !response || !relationshipType) {
      toast.error('Complete the full flow first.');
      return;
    }

    const relationship = {
      id: Date.now().toString(),
      name: partnerName.trim() || 'Partner',
      type: relationshipType,
      startDate: relationshipStartDate || new Date().toISOString().split('T')[0],
      createdAt: new Date().toISOString(),
      isActive: true,
      pronouns: partnerPronouns.trim() || null,
    };

    const goalsMerged = [...goals, ...(goalCustom.trim() ? [goalCustom.trim()] : [])];
    const painPointsMerged = [...painPoints, ...(painPointCustom.trim() ? [painPointCustom.trim()] : [])];

    const userProfile = {
      attachmentStyle: 'unsure',
      relationshipGoal: goalsMerged[0] || 'understand',
      relationshipGoals: goalsMerged,
      currentPainPoints: painPointsMerged,
      communicationStyle,
      conflictStyle,
      affectionStyle,
      checkInCadence,
      reminderTime,
      stressors: stressors.trim(),
      boundaries: boundaries.trim(),
      supportPreference: supportPreference.trim(),
      pastPatterns: [context, emotion, ...painPointsMerged].slice(0, 6),
      firstAhaMoment: contextDetails || context,
      createdAt: new Date().toISOString(),
    };

    const relationshipProfile = {
      partnerName: relationship.name,
      partnerPronouns: relationship.pronouns,
      relationshipType,
      relationshipStartDate: relationship.startDate,
      goals: goalsMerged,
      painPoints: painPointsMerged,
      communicationStyle,
      conflictStyle,
      affectionStyle,
      checkInCadence,
      reminderTime,
      stressors: stressors.trim(),
      boundaries: boundaries.trim(),
      supportPreference: supportPreference.trim(),
    };

    const notificationSettings = {
      enabled: true,
      dailyReminder: true,
      reminderTime,
    };

    const writesSucceeded =
      safeLocalStorageSet('tension_relationships', JSON.stringify([relationship])) &&
      safeLocalStorageSet('tension_active_relationship', relationship.id) &&
      safeLocalStorageSet('tension_user_profile', JSON.stringify(userProfile)) &&
      safeLocalStorageSet('tension_relationship_profile', JSON.stringify(relationshipProfile)) &&
      safeLocalStorageSet('tension_notification_settings', JSON.stringify(notificationSettings)) &&
      safeLocalStorageSet('tension_onboarding_complete', 'true') &&
      safeLocalStorageSet('onboarding_completed', 'true') &&
      safeLocalStorageSet('tension_mode', 'relationship');

    if (!writesSucceeded) {
      toast.error('Unable to save onboarding on this device.');
      return;
    }

    if (accessToken) {
      try {
        await supabase.auth.updateUser({
          data: {
            tension_onboarding_complete: 'true',
            tension_mode: 'relationship',
            tension_partner_name: relationship.name,
            tension_relationship_type: relationship.type,
            tension_relationship_start_date: relationship.startDate,
            tension_relationship_profile: relationshipProfile,
            tension_user_profile: userProfile,
            tension_checkin_cadence: checkInCadence,
            tension_reminder_time: reminderTime,
          },
        });
      } catch {
        // Non-blocking fallback to local state.
      }
    }

    toast.success('You are ready. Let’s keep this momentum.');
    navigate('/home', { state: { showFirstTimeHint: true }, replace: true });
  };

  return (
    <div className="min-h-screen tension-page-bg flex items-center justify-center px-4 sm:px-6 py-6 sm:py-10 pb-[max(env(safe-area-inset-bottom),1rem)]">
      <div className="max-w-xl w-full space-y-4 sm:space-y-6">
        <header className="text-center space-y-2">
          <Title className="text-[34px]">TENSION</Title>
          <Body className="text-center">Early Access setup in six quick steps</Body>
        </header>

        <div className="rounded-[12px] border border-border bg-card p-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Step {step} of 6</span>
            <span>{progress}%</span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-muted">
            <div
              className="h-2 rounded-full bg-primary transition-all duration-200 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {step === 1 && (
          <Card className="p-6 sm:p-10 space-y-5">
            <Headline className="text-center">Partner and relationship basics</Headline>
            <Input
              value={partnerName}
              onChange={(e) => setPartnerName(e.target.value)}
              placeholder="Partner name"
              maxLength={50}
            />
            <Input
              value={partnerPronouns}
              onChange={(e) => setPartnerPronouns(e.target.value)}
              placeholder="Pronouns (optional)"
              maxLength={30}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {RELATIONSHIP_TYPES.map((option) => (
                <ChoiceChip
                  key={option.id}
                  onClick={() => setRelationshipType(option.id)}
                  selected={relationshipType === option.id}
                >
                  {option.label}
                </ChoiceChip>
              ))}
            </div>
            <Input
              type="date"
              value={relationshipStartDate}
              onChange={(e) => setRelationshipStartDate(e.target.value)}
              placeholder="Relationship start date (optional)"
            />
            <Button className="w-full" onClick={nextFromStepOne}>
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Card>
        )}

        {step === 2 && (
          <Card className="p-6 sm:p-10 space-y-5">
            <Headline className="text-center">Goals and current friction points</Headline>
            <Caption className="uppercase tracking-wide">Choose up to 3 goals</Caption>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {GOAL_OPTIONS.map((goal) => (
                <ChoiceChip
                  key={goal}
                  onClick={() => setGoals((current) => toggleSelection(current, goal, 3))}
                  selected={goals.includes(goal)}
                >
                  {goal}
                </ChoiceChip>
              ))}
            </div>
            <Input
              value={goalCustom}
              onChange={(e) => setGoalCustom(e.target.value)}
              placeholder="Custom goal (optional)"
              maxLength={80}
            />
            <Caption className="uppercase tracking-wide">Common pain points (up to 3)</Caption>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PAIN_POINT_OPTIONS.map((item) => (
                <ChoiceChip
                  key={item}
                  onClick={() => setPainPoints((current) => toggleSelection(current, item, 3))}
                  selected={painPoints.includes(item)}
                >
                  {item}
                </ChoiceChip>
              ))}
            </div>
            <Input
              value={painPointCustom}
              onChange={(e) => setPainPointCustom(e.target.value)}
              placeholder="Custom pain point (optional)"
              maxLength={80}
            />
            <div className="flex gap-3">
              <Button variant="secondary" className="flex-1" onClick={() => goToStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button className="flex-1" onClick={nextFromStepTwo}>
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
        )}

        {step === 3 && (
          <Card className="p-6 sm:p-10 space-y-5">
            <Headline className="text-center">Communication and conflict dynamics</Headline>
            <Caption className="uppercase tracking-wide">Communication style</Caption>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {COMMUNICATION_STYLES.map((option) => (
                <ChoiceChip key={option} onClick={() => setCommunicationStyle(option)} selected={communicationStyle === option}>
                  {option}
                </ChoiceChip>
              ))}
            </div>
            <Caption className="uppercase tracking-wide">Conflict style</Caption>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CONFLICT_STYLES.map((option) => (
                <ChoiceChip key={option} onClick={() => setConflictStyle(option)} selected={conflictStyle === option}>
                  {option}
                </ChoiceChip>
              ))}
            </div>
            <Caption className="uppercase tracking-wide">Affection preference</Caption>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {AFFECTION_STYLES.map((option) => (
                <ChoiceChip key={option} onClick={() => setAffectionStyle(option)} selected={affectionStyle === option}>
                  {option}
                </ChoiceChip>
              ))}
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" className="flex-1" onClick={() => goToStep(2)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button className="flex-1" onClick={nextFromStepThree}>
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
        )}

        {step === 4 && (
          <Card className="p-6 sm:p-10 space-y-5">
            <Headline className="text-center">Habits, reminders, and context</Headline>
            <Caption className="uppercase tracking-wide">Check-in cadence</Caption>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {CADENCE_OPTIONS.map((option) => (
                <ChoiceChip key={option} onClick={() => setCheckInCadence(option)} selected={checkInCadence === option}>
                  {option}
                </ChoiceChip>
              ))}
            </div>
            <div>
              <Caption className="uppercase tracking-wide">Reminder time</Caption>
              <Input type="time" value={reminderTime} onChange={(e) => setReminderTime(e.target.value)} />
            </div>
            <Input
              value={stressors}
              onChange={(e) => setStressors(e.target.value)}
              placeholder="Main stressors (optional)"
              maxLength={120}
            />
            <Input
              value={boundaries}
              onChange={(e) => setBoundaries(e.target.value)}
              placeholder="Important boundaries (optional)"
              maxLength={120}
            />
            <Input
              value={supportPreference}
              onChange={(e) => setSupportPreference(e.target.value)}
              placeholder="How you want support when tense (optional)"
              maxLength={120}
            />
            <div className="flex gap-3">
              <Button variant="secondary" className="flex-1" onClick={() => goToStep(3)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button className="flex-1" onClick={nextFromStepFour}>
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
        )}

        {step === 5 && (
          <Card className="p-6 sm:p-10 space-y-6">
            <Headline className="text-center">Run your first guided moment</Headline>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CONTEXT_OPTIONS.map((option) => (
                <ChoiceChip key={option.id} onClick={() => setContext(option.id)} selected={context === option.id}>
                  {option.label}
                </ChoiceChip>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {EMOTION_OPTIONS.map((option) => (
                <ChoiceChip key={option.id} onClick={() => setEmotion(option.id)} selected={emotion === option.id}>
                  {option.label}
                </ChoiceChip>
              ))}
            </div>
            <Input
              value={contextDetails}
              onChange={(e) => setContextDetails(e.target.value)}
              placeholder="Optional: add one short detail"
              maxLength={180}
            />
            <div className="flex gap-3">
              <Button variant="secondary" className="flex-1" onClick={() => goToStep(4)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button className="flex-1" onClick={() => void runTensionEngine()} disabled={!emotion || !context || isLoadingResponse}>
                {isLoadingResponse ? 'Reading moment...' : 'Get response'}
                {!isLoadingResponse && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </div>
          </Card>
        )}

        {step === 6 && (
          <Card className="p-6 sm:p-10 space-y-6">
            <Headline className="text-center">Here is what to say next</Headline>

            <div className="rounded-[12px] border border-border bg-card p-3">
              <Caption className="uppercase tracking-wide">Live state</Caption>
              <p className="mt-1 text-sm font-medium text-foreground">
                {isLoadingResponse
                  ? `thinking -> generating -> responding (${livePhaseLabel || 'in progress'})`
                  : livePhaseLabel || 'response ready'}
              </p>
            </div>

            {response?.insight ? (
              <div
                className={`rounded-[12px] border border-border bg-card p-4 transition-all duration-200 ease-out ${
                  responseRevealStage >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                }`}
              >
                <Caption className="uppercase tracking-wide text-primary">Insight</Caption>
                <p className="mt-1 text-sm text-foreground">{response.insight}</p>
              </div>
            ) : partialResponse?.insight ? (
              <div className="rounded-[12px] border border-border bg-card p-4">
                <Caption className="uppercase tracking-wide text-primary">Insight (live)</Caption>
                <p className="mt-1 text-sm text-foreground">{partialResponse.insight}</p>
              </div>
            ) : null}

            {response?.reframe ? (
              <div
                className={`rounded-[12px] border border-border bg-card p-4 transition-all duration-200 ease-out ${
                  responseRevealStage >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                }`}
              >
                <Caption className="uppercase tracking-wide text-primary">Reframe</Caption>
                <p className="mt-1 text-sm text-foreground">{response.reframe}</p>
              </div>
            ) : null}

            {response?.suggestedMessages?.length ? (
              <div
                className={`rounded-[12px] border border-border bg-card p-4 transition-all duration-200 ease-out ${
                  responseRevealStage >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                }`}
              >
                <Caption className="uppercase tracking-wide text-primary">Suggested message</Caption>
                {response.suggestedMessages.map((msg, idx) => (
                  <p key={msg} className={`text-sm ${idx === 0 ? 'mt-1' : 'mt-2'} text-foreground`}>
                    &quot;{msg}&quot;
                  </p>
                ))}
              </div>
            ) : partialResponse?.suggestedMessages?.length ? (
              <div className="rounded-[12px] border border-border bg-card p-4">
                <Caption className="uppercase tracking-wide text-primary">Suggested message (live)</Caption>
                {partialResponse.suggestedMessages.map((msg) => (
                  <p key={msg} className="mt-1 text-sm text-foreground">
                    &quot;{msg}&quot;
                  </p>
                ))}
              </div>
            ) : null}

            {response?.groundingLine ? (
              <p
                className={`text-sm text-muted-foreground text-center transition-all duration-200 ease-out ${
                  responseRevealStage >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                }`}
              >
                {response.groundingLine}
              </p>
            ) : null}

            {response ? (
              <div className="rounded-[12px] border border-border bg-card p-4 space-y-2">
                <Caption className="uppercase tracking-wide">Share formats</Caption>
                <div className="grid grid-cols-1 gap-2">
                  <Button
                    variant="secondary"
                    className="justify-start"
                    onClick={() => void copyShareValue(response.sharing.sendThis, 'Copied send-ready message.')}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Send this
                  </Button>
                  <Button
                    variant="secondary"
                    className="justify-start"
                    onClick={() => void shareMoment(response.sharing.shareMomentQuote)}
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share this moment
                  </Button>
                  <Button
                    variant="secondary"
                    className="justify-start"
                    onClick={() => void copyShareValue(response.sharing.povCaption, 'Copied POV caption.')}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Copy POV caption
                  </Button>
                </div>
              </div>
            ) : null}

            <div className="flex gap-3">
              <Button variant="secondary" className="flex-1" onClick={() => goToStep(5)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button className="flex-1" onClick={() => void finishOnboarding()} disabled={!response}>
                Continue
                <Check className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
